import { CampaignStatus, logger, Tooltip } from '@/ui'
import { CampaignDataStatus } from '@jobilla/nexus-sdk'
import { differenceInDays, format } from 'date-fns'
import type { TFunction } from 'i18next'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export type CampaignStatusBadgeProps = {
	status: CampaignStatus
	startedAt?: Date | null
	endsAt?: Date | null
	updatedAt?: Date | null
	isEvergreen?: boolean
}

function timeAgo(date: Date, t: TFunction): string {
	const diffMs = Date.now() - date.getTime()
	const diffMinutes = Math.floor(diffMs / 60000)
	const diffHours = Math.floor(diffMinutes / 60)
	const diffDays = Math.floor(diffHours / 24)
	const diffWeeks = Math.floor(diffDays / 7)
	const diffMonths = Math.floor(diffDays / 30)

	if (diffMinutes < 2) { return t('dashboard.badge.time.justNow') }
	if (diffMinutes < 60) { return t('dashboard.badge.time.mAgo', { count: diffMinutes }) }
	if (diffHours < 24) { return t('dashboard.badge.time.hAgo', { count: diffHours }) }
	if (diffDays < 7) { return t('dashboard.badge.time.dAgo', { count: diffDays }) }
	if (diffWeeks < 5) { return t('dashboard.badge.time.wAgo', { count: diffWeeks }) }
	if (diffMonths < 12) { return t('dashboard.badge.time.moAgo', { count: diffMonths }) }
	return t('dashboard.badge.time.yAgo', { count: Math.floor(diffMonths / 12) })
}

type BadgeConfig = {
	dotClass: string
	labelClass: string
	label: string
	timeText: string
}

function getConfig(
	status: CampaignStatus,
	startedAt: Date | null | undefined,
	endsAt: Date | null | undefined,
	updatedAt: Date | null | undefined,
	isEvergreen: boolean,
	t: TFunction,
): BadgeConfig {
	// Evergreen campaigns have no meaningful schedule; ignore any stored dates
	// (a career-site-only campaign may still carry stale start/end dates).
	if (isEvergreen) {
		startedAt = null
		endsAt = null
	}

	const daysLeft = endsAt != null ? Math.max(0, differenceInDays(endsAt, new Date())) : 0
	const isEndingSoon = status === 'active' && endsAt != null && daysLeft <= 3

	switch (status) {
		case 'draft':
			return {
				dotClass: 'bg-amber-400',
				labelClass: 'text-amber-700',
				label: t('dashboard.badge.status.draft'),
				timeText: updatedAt ? t('dashboard.badge.time.lastEdited', { time: timeAgo(updatedAt, t) }) : '',
			}

		case 'review':
		case 'publishing':
			return {
				dotClass: 'bg-purple-500',
				labelClass: 'text-purple-700',
				label: t('dashboard.badge.status.inReview'),
				timeText: updatedAt ? t('dashboard.badge.time.began', { time: timeAgo(updatedAt, t) }) : '',
			}

		case 'active':
			if (isEndingSoon) {
				return {
					dotClass: 'bg-amber-400',
					labelClass: 'text-amber-700',
					label: t('dashboard.badge.status.endingSoon'),
					timeText: t('dashboard.badge.time.dayLeft', { count: daysLeft }),
				}
			}
			return {
				dotClass: 'bg-green-500',
				labelClass: 'text-green-700',
				label: t('dashboard.badge.status.active'),
				timeText: isEvergreen
					? t('dashboard.ongoing')
					: endsAt
					? t('dashboard.badge.time.dayLeft', { count: daysLeft })
					: '',
			}

		case 'completed':
			return {
				dotClass: 'bg-grey-400',
				labelClass: 'text-grey-500',
				label: t('dashboard.badge.status.completed'),
				timeText: endsAt ? timeAgo(endsAt, t) : '',
			}

		default:
			return {
				dotClass: 'bg-grey-400',
				labelClass: 'text-grey-500',
				label: status,
				timeText: '',
			}
	}
}

export function CampaignStatusBadge(
	{ status, startedAt, endsAt, updatedAt, isEvergreen = false }: CampaignStatusBadgeProps,
) {
	const { t } = useTranslation()
	const { dotClass, labelClass, label, timeText } = getConfig(status, startedAt, endsAt, updatedAt, isEvergreen, t)

	return (
		<DateRange startedAt={isEvergreen ? null : startedAt} endsAt={isEvergreen ? null : endsAt} status={status}>
			<div className="flex items-center gap-1.5 text-xs min-w-0">
				<span className={`size-1.5 shrink-0 rounded-full ${dotClass}`} />
				<span className={`font-medium shrink-0 ${labelClass}`}>{label}</span>
				{timeText && (
					<>
						<span className="text-grey-300 shrink-0">·</span>
						<span className="text-grey-400 truncate">{timeText}</span>
					</>
				)}
			</div>
		</DateRange>
	)
}

function DateRange({ children, startedAt, endsAt, status }: {
	children: ReactNode
	startedAt?: Date | null
	endsAt?: Date | null
	status?: CampaignDataStatus
}) {
	const { t } = useTranslation()

	if (!startedAt && !endsAt) { return <>{children}</> }

	const isEndingSoon = status === 'active' && endsAt != null && differenceInDays(endsAt, new Date()) <= 3
	const isActive = status === 'active' || isEndingSoon
	const isCompleted = status === 'completed'

	let tooltipContent: string
	if (startedAt && endsAt) {
		const start = format(startedAt, 'dd.MM.yyyy')
		const end = format(endsAt, 'dd.MM.yyyy')
		if (isActive) {
			const days = differenceInDays(endsAt, new Date())
			tooltipContent = `${start} → ${end} · ${t('dashboard.badge.tooltip.runningFor', { count: days })}`
		} else if (isCompleted) {
			const days = differenceInDays(new Date(), endsAt)
			tooltipContent = `${start} → ${end} · ${t('dashboard.badge.tooltip.endedAgo', { count: days })}`
		} else {
			tooltipContent = `${start} → ${end}`
		}
	} else if (startedAt) {
		tooltipContent = t('dashboard.badge.tooltip.started', { date: format(startedAt, 'dd.MM.yyyy') })
	} else {
		tooltipContent = t('dashboard.badge.tooltip.ends', { date: format(endsAt!, 'dd.MM.yyyy') })
	}

	return (
		<Tooltip content={tooltipContent}>
			<span
				className="text-xs text-grey-400 cursor-default"
				onMouseEnter={() => {
					logger.track('Dashboard: Date tooltip viewed', {
						data: {
							campaign_id: undefined,
							campaign_status: status,
						},
					})
				}}
			>
				{children}
			</span>
		</Tooltip>
	)
}
